import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Send, Sparkles, FileText } from "lucide-react";
import api, { API, TOKEN_KEY, apiErr } from "@/lib/api";
import { rp, num, pcs, pct, monthOptions } from "@/lib/format";
import PeriodPicker, { todayWib, weekRange } from "@/components/PeriodPicker";
import { PageTitle, Panel, Stat } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Report() {
  const [pv, setPv] = useState({ period: "BULANAN", date: todayWib() });
  const month = pv.date.slice(0, 7);
  const range = pv.period === "HARIAN" ? [pv.date, pv.date] : pv.period === "MINGGUAN" ? weekRange(pv.date) : null;
  const rangeQ = range ? `date_from=${range[0]}&date_to=${range[1]}` : `month=${month}`;
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const months = monthOptions(12);

  const runQuery = async (text) => {
    try {
      const { data } = await api.post("/query", { text });
      setResult(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => { runQuery(`TOTAL ${months[0].label.split(" ")[0].toUpperCase()}`); }, []);

  const download = (path, filename, label) => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); }).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url); toast.success(`Export ${label} berhasil`);
      }).catch(() => toast.error("Export gagal"));
  };
  const exportLedger = (fmt) => download(`/export/ledger?fmt=${fmt}&${rangeQ}`, `ledger_${range ? range[0] + "_" + range[1] : month}.${fmt}`, fmt.toUpperCase());
  const exportPdf = () => download(`/export/report-pdf?${pv.period === "BULANAN" ? `month=${month}` : `period=${pv.period}&date=${pv.date}`}`, `laporan_${range ? range[0] + "_" + range[1] : month}.pdf`, "PDF");

  const examples = ["WIP", "YURI TODAY", "YURI SEPTEMBER", "BON P001", "REJECT SEPTEMBER", "TOTAL SEPTEMBER", "HISTORY P001"];

  return (
    <div>
      <PageTitle title="Laporan & Query Cepat" desc="Rekapitulasi harian/bulanan, export, dan mesin query fleksibel (sama seperti Telegram)." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Panel title="Export Data">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <div className="text-xs text-slate-500 mb-1">Periode</div>
              <PeriodPicker value={pv} onChange={setPv} testId="report-period" />
            </div>
            <Button variant="outline" onClick={() => exportLedger("xlsx")} data-testid="report-export-xlsx"><Download className="w-4 h-4 mr-1.5" />Excel (XLSX)</Button>
            <Button variant="outline" onClick={() => exportLedger("csv")} data-testid="report-export-csv"><Download className="w-4 h-4 mr-1.5" />CSV</Button>
            <Button onClick={exportPdf} className="bg-slate-900 text-white" data-testid="report-export-pdf"><FileText className="w-4 h-4 mr-1.5" />Laporan PDF</Button>
          </div>
          <p className="text-xs text-slate-400 mt-3">Export General Ledger untuk bulan terpilih (semua worker & tipe transaksi confirmed/pending).</p>
        </Panel>

        <Panel title="Query Cepat">
          <div className="flex gap-2">
            <Input placeholder="Ketik: WIP P001 / YURI SEPTEMBER / BON P001…" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runQuery(q)} data-testid="report-query-input" />
            <Button onClick={() => runQuery(q)} className="bg-slate-900 text-white" data-testid="report-query-btn"><Send className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {examples.map((ex) => (
              <button key={ex} onClick={() => { setQ(ex); runQuery(ex); }} className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium">{ex}</button>
            ))}
          </div>
        </Panel>
      </div>

      {result && (
        <Panel title={<span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Hasil: {result.title}</span>}>
          {result.type === "wip" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Stat label="Total Ambil" value={pcs(result.data.total_ambil)} tone="blue" />
              <Stat label="Total Setor" value={pcs(result.data.total_setor)} tone="emerald" />
              <Stat label="Open WIP" value={pcs(result.data.open_wip)} tone="amber" sub={result.data.perlu_rekonsiliasi ? `PERLU REKONSILIASI · SETOR MELEBIHI AMBIL ${pcs(result.data.selisih_rekonsiliasi)}` : undefined} />
            </div>
          )}
          {result.type === "metrics" && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Keluar" value={pcs(result.data.total_ambil)} tone="blue" />
              <Stat label="Setor" value={pcs(result.data.total_setor)} tone="emerald" />
              <Stat label="Good" value={pcs(result.data.total_good)} tone="emerald" />
              <Stat label="Reject" value={pcs(result.data.total_reject)} sub={pct(result.data.reject_rate)} tone="rose" />
              <Stat label="Open WIP" value={pcs(result.data.open_wip)} tone="amber" sub={result.data.wip_perlu_rekonsiliasi ? `PERLU REKONSILIASI · SETOR MELEBIHI AMBIL ${pcs(result.data.wip_selisih_rekonsiliasi)}` : undefined} />
              <Stat label="Total Bon" value={rp(result.data.total_bon)} tone="indigo" />
              <Stat label="Gross Ongkos" value={rp(result.data.gross_sewing_pay)} />
              <Stat label="Outstanding" value={rp(result.data.outstanding_payable)} tone="rose" />
            </div>
          )}
          {result.type === "history" && (
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {result.items.map((t) => (
                <div key={t.transaction_id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                  <span className="text-slate-500 mono text-xs">{t.transaction_date} · {t.transaction_type}</span>
                  <span className="text-slate-700">{t.product_name || t.bon_item_name || "-"} · {num(t.quantity)}</span>
                </div>
              ))}
            </div>
          )}
          {result.type === "error" && <div className="text-rose-600 text-sm">{result.message}</div>}
          {result.type === "lookup" && (
            <div className="space-y-2" data-testid="query-lookup">
              <div className="text-sm font-semibold text-slate-800" data-testid="query-lookup-message">{result.message}</div>
              {(result.rows || []).map((r, i) => {
                const d = r.data || r; const id = r.id || d.loan_id || d.rjp_id || d.repair_id || d.transaction_id;
                if (r.found === false) return <div key={id} className="text-xs text-slate-400 mono" data-testid={`lookup-${id}`}>{id} — tidak ditemukan</div>;
                return <div key={id || i} className="text-xs border border-slate-200 rounded p-2 mono" data-testid={`lookup-${id}`}>
                  <b>{id}</b> · {d.worker_id || d.pengepul_id} {d.worker_name || d.pengepul_name || ""} · status <b>{d.status}</b>
                  {d.amount != null && <> · Rp{num(d.amount)}</>}{d.qty != null && <> · qty {num(d.qty)}</>}{d.source_trx_id && <> · asal {d.source_trx_id}</>}
                  {d.totalan_id && <> · totalan {d.totalan_id.slice(0, 8)}</>}{d.ledger_trx_id && <> · ledger {d.ledger_trx_id}</>}
                </div>;
              })}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
